import React, { useEffect, useMemo } from "react";
import "./Steps.css";
import AddressAutocomplete from "../AddressAutocomplete/AddressAutocomplete";

function Step4AssemblyDistribution({
  formData,
  countries,
  transportModes,
  transportModesLoading,
  transportModesError,
  updateFormData,
  addTransportLeg,
  removeTransportLeg,
  validationErrors = [],
}) {
  const hasError = (fieldKeyword) => {
    return validationErrors.some((err) =>
      err.toLowerCase().includes(fieldKeyword.toLowerCase())
    );
  };

  const hasLegError = (index, fieldKeyword) => {
    return validationErrors.some((err) => {
      const lower = err.toLowerCase();
      return lower.includes(fieldKeyword.toLowerCase()) && lower.includes(`leg ${index + 1}`);
    });
  };

  const isFilled = (value) => {
    return value !== "" && value !== null && value !== undefined;
  };

  // Auto-fix: normalize assemblyLocation if it's an object
  useEffect(() => {
    const location = formData.assemblyDistribution.manufacturing.assemblyLocation;
    if (location && typeof location === "object" && location !== null) {
      const normalizedLocation = location.code || "";
      updateFormData("assemblyDistribution", "manufacturing", {
        ...formData.assemblyDistribution.manufacturing,
        assemblyLocation: normalizedLocation,
      });
    }
  }, [formData.assemblyDistribution.manufacturing, updateFormData]);

  const TRANSPORT_OPTIONS = useMemo(() => {
    return (transportModes || []).map((m) => ({
      value: m.value,
      label: m.label,
      factor: Number(m.factor_kgco2_per_tkm || 0),
    }));
  }, [transportModes]);

  const toRad = (deg) => (deg * Math.PI) / 180;
  const haversineKm = (a, b) => {
    if (!a || !b) return 0;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return Math.round(R * c);
  };

  // Returns a distance multiplier based on transport mode to approximate real-world routes
  const getModeMultiplier = (mode) => {
    if (!mode) return 1.2;
    const m = mode.toLowerCase();
    if (m.includes("air")) return 1.0;
    if (m.includes("sea") || m.includes("ship") || m.includes("ferry") || m.includes("boat")) return 1.2;
    if (m.includes("road") || m.includes("truck") || m.includes("hgv") || m.includes("van")) return 1.3;
    if (m.includes("rail") || m.includes("train")) return 1.15;
    return 1.2;
  };

  const getModeLabel = (mode) => {
    if (!mode) return "transport";
    const m = mode.toLowerCase();
    if (m.includes("air")) return "air";
    if (m.includes("sea") || m.includes("ship") || m.includes("ferry") || m.includes("boat")) return "sea";
    if (m.includes("road") || m.includes("truck") || m.includes("hgv") || m.includes("van")) return "road";
    if (m.includes("rail") || m.includes("train")) return "rail";
    return "transport";
  };

  const updateLeg = (index, updates) => {
    const legs = [...formData.assemblyDistribution.transportation];
    legs[index] = { ...legs[index], ...updates };
    updateFormData("assemblyDistribution", "transportation", legs);
  };

  const handleModeChange = (index, newMode) => {
    const leg = formData.assemblyDistribution.transportation[index];
    const updates = { mode: newMode };
    // Recalculate distance if both addresses are set
    if (leg.originLat && leg.originLng && leg.destinationLat && leg.destinationLng) {
      const rawKm = haversineKm(
        { lat: leg.originLat, lon: leg.originLng },
        { lat: leg.destinationLat, lon: leg.destinationLng }
      );
      updates.distance = Math.round(rawKm * getModeMultiplier(newMode));
      updates.distanceAutoCalculated = true;
    }
    updateLeg(index, updates);
  };

  const handleAddressSelect = (index, field, result) => {
    const leg = formData.assemblyDistribution.transportation[index];
    const isOrigin = field === "origin";
    const updates = isOrigin
      ? { originAddress: result?.address || "", originLat: result?.lat || null, originLng: result?.lng || null }
      : { destinationAddress: result?.address || "", destinationLat: result?.lat || null, destinationLng: result?.lng || null };

    const originLat = isOrigin ? result?.lat : leg.originLat;
    const originLng = isOrigin ? result?.lng : leg.originLng;
    const destLat = isOrigin ? leg.destinationLat : result?.lat;
    const destLng = isOrigin ? leg.destinationLng : result?.lng;

    if (originLat && originLng && destLat && destLng) {
      const rawKm = haversineKm(
        { lat: originLat, lon: originLng },
        { lat: destLat, lon: destLng }
      );
      const multiplier = getModeMultiplier(leg.mode);
      updates.distance = Math.round(rawKm * multiplier);
      updates.distanceAutoCalculated = true;
    } else {
      // One address cleared — reset auto-calc flag only if distance was auto
      if (leg.distanceAutoCalculated) {
        updates.distance = "";
        updates.distanceAutoCalculated = false;
      }
    }
    updateLeg(index, updates);
  };

  const handleDistanceChange = (index, value) => {
    const km = value === "" ? "" : Math.max(0, Number(value));
    updateLeg(index, { distance: km, distanceAutoCalculated: false });
  };

  const legs = formData.assemblyDistribution.transportation;

  return (
    <div className="step-container">
      {/* Header */}
      <div className="step-header">
        <div className="step-number">04</div>
        <div>
          <h2 className="step-title">Assembly & Distribution</h2>
          <p className="step-subtitle">
            Tell us about manufacturing and transportation
          </p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="form-section">
        {/* Manufacturing Section (only if manufactured) */}
        {formData.productInfo.productType === "manufactured" && (
          <>
            <div className="form-field">
              <h3 className="section-title">Manufacturing</h3>
              <p className="section-description">
                Energy consumption and assembly location
              </p>
            </div>

            <div className="conditional-section">
              <div className="form-row">
                <div className="form-field">
                  <label className="field-label">
                    Energy Consumption (kWh)
                    <span className="field-required">*</span>
                  </label>
                  <input
                    type="number"
                    className={`field-input ${
                      hasError("energy consumption") ? "field-input--error" : ""
                    } ${
                      isFilled(formData.assemblyDistribution.manufacturing.energyConsumption)
                        ? "field-input--filled"
                        : ""
                    }`}
                    value={formData.assemblyDistribution.manufacturing.energyConsumption}
                    placeholder="e.g., 0.5"
                    onChange={(e) =>
                      updateFormData("assemblyDistribution", "manufacturing", {
                        ...formData.assemblyDistribution.manufacturing,
                        energyConsumption: e.target.value,
                      })
                    }
                  />
                  {hasError("energy consumption") && (
                    <div className="field-error">Energy consumption is required</div>
                  )}
                </div>

                <div className="form-field">
                  <label className="field-label">
                    Assembly Location
                    <span className="field-required">*</span>
                  </label>
                  <select
                    className={`field-input field-select ${
                      hasError("assembly location") ? "field-input--error" : ""
                    } ${
                      isFilled(formData.assemblyDistribution.manufacturing.assemblyLocation)
                        ? "field-input--filled"
                        : ""
                    }`}
                    value={
                      typeof formData.assemblyDistribution.manufacturing.assemblyLocation === "object" &&
                      formData.assemblyDistribution.manufacturing.assemblyLocation !== null
                        ? formData.assemblyDistribution.manufacturing.assemblyLocation.code || ""
                        : formData.assemblyDistribution.manufacturing.assemblyLocation || ""
                    }
                    onChange={(e) =>
                      updateFormData("assemblyDistribution", "manufacturing", {
                        ...formData.assemblyDistribution.manufacturing,
                        assemblyLocation: e.target.value,
                      })
                    }
                  >
                    {countries.map((country, index) => (
                      <option key={index} value={country.country_code}>
                        {country.country_name}
                      </option>
                    ))}
                  </select>
                  {hasError("assembly location") && (
                    <div className="field-error">Assembly location is required</div>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label className="field-label">Energy Source</label>
                <select
                  className={`field-input field-select ${
                    isFilled(formData.assemblyDistribution.manufacturing.energySource)
                      ? "field-input--filled"
                      : ""
                  }`}
                  value={formData.assemblyDistribution.manufacturing.energySource}
                  onChange={(e) =>
                    updateFormData("assemblyDistribution", "manufacturing", {
                      ...formData.assemblyDistribution.manufacturing,
                      energySource: e.target.value,
                      renewablePercentage: "",
                    })
                  }
                >
                  <option value="grid">Grid Electricity</option>
                  <option value="renewable">Renewable Energy</option>
                  <option value="mixed">Mixed Sources</option>
                </select>
              </div>

              {formData.assemblyDistribution.manufacturing.energySource === "mixed" && (
                <div className="form-field">
                  <label className="field-label">
                    Renewable Energy Percentage (%)
                    <span className="field-required">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className={`field-input ${
                      isFilled(formData.assemblyDistribution.manufacturing.renewablePercentage)
                        ? "field-input--filled"
                        : ""
                    }`}
                    value={formData.assemblyDistribution.manufacturing.renewablePercentage}
                    placeholder="e.g., 20"
                    onChange={(e) => {
                      const val = e.target.value === "" ? "" : Math.min(100, Math.max(0, Number(e.target.value)));
                      updateFormData("assemblyDistribution", "manufacturing", {
                        ...formData.assemblyDistribution.manufacturing,
                        renewablePercentage: val,
                      });
                    }}
                  />
                  <div className="field-hint">
                    Renewable energy has a 0 emission factor. Only the remaining {
                      formData.assemblyDistribution.manufacturing.renewablePercentage !== ""
                        ? 100 - Number(formData.assemblyDistribution.manufacturing.renewablePercentage)
                        : 100
                    }% will contribute to emissions.
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="form-divider"></div>
          </>
        )}

        {/* Transportation Section */}
        <div className="section-header">
          <div>
            <h3 className="section-title">Transportation</h3>
            <p className="section-description">
              {legs.length > 0
                ? `${legs.length} transport ${legs.length === 1 ? "leg" : "legs"} added`
                : "Add shipping legs from origin to destination"}
            </p>
          </div>
          
        </div>

        {legs.map((leg, index) => (
          <div key={index} className="item-card">
            <div className="item-card-header">
              <span className="item-badge">Leg {index + 1}</span>
              {legs.length > 1 && (
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => removeTransportLeg(index)}
                  aria-label="Remove transport leg"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>

            {/* Transport Mode */}
            <div className="form-field">
              <label className="field-label">
                Transport Mode
                <span className="field-required">*</span>
              </label>
              {transportModesLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading transport modes...</p>
                </div>
              ) : transportModesError ? (
                <div className="alert-warning">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 6V10M10 14H10.01M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {transportModesError}
                </div>
              ) : (
                <select
                  className={`field-input field-select ${
                    hasLegError(index, "transport mode") ? "field-input--error" : ""
                  } ${isFilled(leg.mode) ? "field-input--filled" : ""}`}
                  value={leg.mode}
                  onChange={(e) => handleModeChange(index, e.target.value)}
                  disabled={!TRANSPORT_OPTIONS.length}
                >
                  {TRANSPORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {hasLegError(index, "transport mode") && (
                <div className="field-error">Transport mode is required</div>
              )}
            </div>

            {/* Origin & Destination */}
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">
                  Origin
                  <span className="field-required">*</span>
                </label>
                <AddressAutocomplete
                  value={leg.originAddress || ""}
                  onSelect={(result) => handleAddressSelect(index, "origin", result)}
                  placeholder="Type a city, port or address..."
                  inputClassName="field-input"
                  errorClassName="field-input--error"
                  filledClassName="field-input--filled"
                  hasError={hasLegError(index, "origin")}
                />
                {hasLegError(index, "origin") && (
                  <div className="field-error">Origin is required</div>
                )}
              </div>

              <div className="form-field">
                <label className="field-label">
                  Destination
                  <span className="field-required">*</span>
                </label>
                <AddressAutocomplete
                  value={leg.destinationAddress || ""}
                  onSelect={(result) => handleAddressSelect(index, "destination", result)}
                  placeholder="Type a city, port or address..."
                  inputClassName="field-input"
                  errorClassName="field-input--error"
                  filledClassName="field-input--filled"
                  hasError={hasLegError(index, "destination")}
                />
                {hasLegError(index, "destination") && (
                  <div className="field-error">Destination is required</div>
                )}
              </div>
            </div>

            {/* Distance */}
            <div className="form-field">
              <label className="field-label">
                Distance (km)
                <span className="field-required">*</span>
              </label>
              <input
                type="number"
                className={`field-input ${
                  hasLegError(index, "distance") ? "field-input--error" : ""
                } ${isFilled(leg.distance) ? "field-input--filled" : ""}`}
                value={leg.distance}
                placeholder="Auto-calculated or enter manually"
                onChange={(e) => handleDistanceChange(index, e.target.value)}
              />
              {hasLegError(index, "distance") && (
                <div className="field-error">Distance is required</div>
              )}
              {leg.distance && leg.distanceAutoCalculated && !hasLegError(index, "distance") && (
                <div className="field-hint">
                  ✓ Estimated for {getModeLabel(leg.mode)} travel. Overwrite if you know the true distance.
                </div>
              )}
            </div>
          </div>
        ))}

        <button
            type="button"
            className="btn-add"
            onClick={addTransportLeg}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add Transport Leg
          </button>

        {legs.length > 1 && (
          <div className="info-box">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 14V10M10 6H10.01M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div>
              <div className="info-box-title">Total transport emissions</div>
              <div className="info-box-text">Emissions from all {legs.length} legs will be summed for the total transport footprint.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Step4AssemblyDistribution;
